import { Empty, Popover, Tree } from 'antd';
import { useMemo, useState } from 'react';

import type { NFunnels } from '@app/types';

import { StageColorSquare } from '../StageColorSquare';

import type { TreeDataNode, TreeProps } from 'antd';
import type { FC, Key, ReactNode } from 'react';

import './FunnelStageTreeSelect.scss';

interface FunnelStageTreeSelectProps {
  funnels: NFunnels.SalesFunnelFilterDto[];
  selectedStageId?: number | null;
  disabled?: boolean;
  emptyText?: string;
  children: ReactNode;
  onSelect(stageId: number): void;
}

const UNNAMED_FUNNEL = 'Без названия';
const UNNAMED_STAGE = 'Без названия';

export const FunnelStageTreeSelect: FC<FunnelStageTreeSelectProps> = ({
  funnels,
  selectedStageId = null,
  emptyText = 'Нет доступных этапов',
  children,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Этапы каждой воронки по умолчанию свёрнуты. Состояние сбрасывается при
  // каждом новом открытии поповера, чтобы всегда показывать исходный вид.
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const treeData = useMemo<TreeDataNode[]>(
    () =>
      funnels
        .filter((funnel) => (funnel.Stages?.length ?? 0) > 0)
        .map((funnel) => ({
          key: `funnel-${funnel.Id}`,
          title: funnel.Name || UNNAMED_FUNNEL,
          selectable: false,
          children: (funnel.Stages ?? []).map((stage) => ({
            key: stage.Id,
            title: (
              <span className="FunnelStageTreeSelect__option">
                <StageColorSquare color={stage.Color} />
                <span className="FunnelStageTreeSelect__option-name">
                  {stage.Name || UNNAMED_STAGE}
                </span>
              </span>
            ),
          })),
        })),
    [funnels]
  );

  const selectHandler: TreeProps['onSelect'] = (_keys, { node }) => {
    if (typeof node.key !== 'number') return;

    setIsOpen(false);
    onSelect(node.key);
  };

  const expandHandler: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys);
  };

  const openChangeHandler = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);

    if (nextIsOpen) setExpandedKeys([]);
  };

  const content =
    treeData.length > 0 ? (
      <Tree
        className="FunnelStageTreeSelect__tree"
        treeData={treeData}
        blockNode
        expandAction="click"
        expandedKeys={expandedKeys}
        selectedKeys={selectedStageId !== null ? [selectedStageId] : []}
        onExpand={expandHandler}
        onSelect={selectHandler}
      />
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyText}
        className="FunnelStageTreeSelect__empty"
      />
    );

  return (
    <Popover
      content={content}
      open={isOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      classNames={{ root: 'FunnelStageTreeSelect__overlay' }}
      onOpenChange={openChangeHandler}
    >
      {children}
    </Popover>
  );
};
